'use client';

import { useState, useEffect, useCallback } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useChat } from '@/lib/hooks/useChat';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ContactPerson {
  name: string;
  role: string;
  phone?: string;
  email?: string;
}

interface ContactDirectory {
  reported_by: ContactPerson;
  assigned_to: ContactPerson;
  department_head: ContactPerson;
  affected_job_pm: ContactPerson;
}

interface AssignmentHistoryEntry {
  date_time: string;
  assigned_from: string;
  assigned_to: string;
  reason: string;
}

interface TimelineEntry {
  timestamp: string;
  actor: string;
  action: string;
  type: 'created' | 'note' | 'reassigned' | 'status_change' | 'escalated';
}

interface Escalation {
  id: string;
  ticket_id: string;
  title: string;
  description: string;
  source_department: string;
  priority: string;
  status: string;
  assigned_to_id: string;
  assigned_to_name: string;
  reported_by_id: string;
  reported_by_name: string;
  affected_job_id: string;
  affected_job_address: string;
  contact_directory: ContactDirectory;
  assignment_history: AssignmentHistoryEntry[];
  timeline_log: TimelineEntry[];
  created_at: string;
  updated_at: string;
  response_due: string | null;
  resolution_due: string | null;
}

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  user_role: string;
}

// ─── Comment Thread Types ─────────────────────────────────────────────────────

interface Comment {
  id: string;
  author: string;
  authorInitials: string;
  text: string;
  timestamp: string;
  type: 'comment' | 'action';
  actionType?: 'reassigned' | 'priority_changed' | 'closed';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateTime = (dt: string) => {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
};

const timeAgo = (dt: string) => {
  if (!dt) return '—';
  const diff = Date.now() - new Date(dt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const getPriorityColor = (priority: string) => {
  if (priority === 'high') return { bg: '#FEE2E2', text: '#EF4444', dot: '#EF4444' };
  if (priority === 'medium') return { bg: '#FEF3C7', text: '#F59E0B', dot: '#F59E0B' };
  return { bg: '#DCFCE7', text: '#10B981', dot: '#10B981' };
};

const getStatusColor = (status: string) => {
  if (status === 'open') return { bg: '#FEE2E2', text: '#EF4444' };
  if (status === 'in_progress') return { bg: '#DBEAFE', text: '#E89A3C' };
  if (status === 'resolved') return { bg: '#DCFCE7', text: '#10B981' };
  if (status === 'closed') return { bg: '#F3F4F6', text: '#6B7280' };
  return { bg: '#F3F4F6', text: '#6B7280' };
};

const getStatusLabel = (status: string) => {
  const map: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    resolved: 'Resolved',
    closed: 'Closed',
  };
  return map[status] || status;
};

const getDeptColor = (dept: string) => {
  const map: Record<string, string> = {
    Logistics: '#1C2E5E',
    Billing: '#F59E0B',
    Warranty: '#EF4444',
    Operations: '#8B5CF6',
    'Supply Chain': '#E89A3C',
    'Quality Control': '#10B981',
    Finance: '#EC4899',
    Executive: '#7C3AED',
  };
  return map[dept] || '#6B7280';
};

const getTimelineIcon = (type: string) => {
  const map: Record<string, string> = {
    created: 'PlusCircleIcon',
    note: 'ChatBubbleLeftIcon',
    reassigned: 'ArrowPathIcon',
    status_change: 'CheckCircleIcon',
    escalated: 'ExclamationTriangleIcon',
  };
  return map[type] || 'InformationCircleIcon';
};

const getTimelineColor = (type: string) => {
  const map: Record<string, string> = {
    created: '#E89A3C',
    note: '#6B7280',
    reassigned: '#F59E0B',
    status_change: '#10B981',
    escalated: '#EF4444',
  };
  return map[type] || '#6B7280';
};

const getInitials = (name: string) => {
  const parts = name.split(' ');
  return parts.map(p => p.charAt(0)).join('').toUpperCase().slice(0, 2);
};

const isBreached = (dt: string | null | undefined): boolean => {
  if (!dt) return false;
  return new Date(dt).getTime() < Date.now();
};

const formatDueDate = (dt: string | null | undefined): string => {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
};

// ─── AI Escalation Summary Component ─────────────────────────────────────────

interface AIEscalationSummaryProps {
  ticket: Escalation;
}

function AIEscalationSummary({ ticket }: AIEscalationSummaryProps) {
  const [summary, setSummary] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { response, isLoading, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);
  const [hasFetched, setHasFetched] = useState(false);

  const cacheKey = `esc_summary_${ticket.ticket_id}`;

  const generateSummary = useCallback(() => {
    if (hasFetched) return;
    setHasFetched(true);

    // Check session cache
    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { text, ts } = JSON.parse(cached);
        if (Date.now() - ts < 60 * 60 * 1000) {
          setSummary(text);
          return;
        }
      }
    } catch { /* ignore */ }

    setLoading(true);

    const timelineText = (ticket.timeline_log || [])
      .map(t => `[${t.timestamp}] ${t.actor}: ${t.action}`)
      .join('\n');

    const assignmentText = (ticket.assignment_history || [])
      .map(a => `${a.date_time}: ${a.assigned_from} → ${a.assigned_to} (${a.reason})`)
      .join('\n');

    sendMessage([
      {
        role: 'system',
        content: 'You are an escalation management AI for NextRoof 360, a roofing company. Analyze the escalation ticket and provide: 1) A 1-2 sentence concise summary of the situation, and 2) A clear recommended next action. Format as: "Summary: [text] | Next Action: [text]". Be specific and actionable.',
      },
      {
        role: 'user',
        content: `Escalation Ticket: ${ticket.ticket_id}
Title: ${ticket.title}
Department: ${ticket.source_department}
Priority: ${ticket.priority}
Status: ${ticket.status}
Description: ${ticket.description}

Timeline Log:
${timelineText || 'No timeline entries'}

Assignment History:
${assignmentText || 'No assignment history'}`,
      },
    ], { max_completion_tokens: 200 });
  }, [ticket, hasFetched, sendMessage, cacheKey]);

  useEffect(() => {
    generateSummary();
  }, [generateSummary]);

  useEffect(() => {
    if (response && !isLoading) {
      setSummary(response);
      setLoading(false);
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify({ text: response, ts: Date.now() }));
      } catch { /* ignore */ }
    }
  }, [response, isLoading, cacheKey]);

  const isGenerating = loading || isLoading;

  // Parse summary and next action
  const summaryPart = summary?.split('| Next Action:')[0]?.replace('Summary:', '').trim() || '';
  const nextActionPart = summary?.split('| Next Action:')[1]?.trim() || '';

  return (
    <section
      className="rounded-xl overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0FDF4 100%)', border: '1px solid #BFDBFE' }}
    >
      <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: '#BFDBFE', background: 'rgba(3,52,148,0.04)' }}>
        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
          <Icon name="SparklesIcon" size={12} className="text-white" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#1C2E5E' }}>
          SmartPick™ AI Escalation Summary
        </span>
        {isGenerating && (
          <div className="w-3 h-3 rounded-full border border-[#E89A3C] border-t-transparent animate-spin ml-auto" />
        )}
        {!isGenerating && summary && (
          <button
            onClick={() => {
              setHasFetched(false);
              setSummary(null);
              try { sessionStorage.removeItem(cacheKey); } catch { /* ignore */ }
            }}
            className="ml-auto flex items-center gap-1 text-xs hover:opacity-70 transition-opacity"
            style={{ color: '#1C2E5E' }}
          >
            <Icon name="ArrowPathIcon" size={11} />
            Refresh
          </button>
        )}
      </div>
      <div className="px-5 py-4">
        {isGenerating ? (
          <div className="space-y-2.5">
            <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.1)', width: '92%' }} />
            <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(3,52,148,0.07)', width: '78%' }} />
            <div className="mt-3 h-3 rounded-full animate-pulse" style={{ background: 'rgba(232,154,60,0.12)', width: '65%' }} />
            <p className="text-[10px] mt-1" style={{ color: '#93C5FD' }}>Analyzing ticket data…</p>
          </div>
        ) : summary ? (
          <div className="space-y-3">
            {summaryPart && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: '#6B7280' }}>Situation Summary</p>
                <p className="text-sm leading-relaxed" style={{ color: '#1E3A5F' }}>{summaryPart}</p>
              </div>
            )}
            {nextActionPart && (
              <div className="flex items-start gap-2 rounded-lg px-3 py-2.5" style={{ background: 'rgba(232,154,60,0.08)', border: '1px solid rgba(232,154,60,0.2)' }}>
                <Icon name="ArrowRightCircleIcon" size={16} style={{ color: '#E89A3C', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#E89A3C' }}>Recommended Next Action</p>
                  <p className="text-sm font-semibold" style={{ color: '#1C2E5E' }}>{nextActionPart}</p>
                </div>
              </div>
            )}
            {!summaryPart && !nextActionPart && (
              <p className="text-sm leading-relaxed" style={{ color: '#1E3A5F' }}>{summary}</p>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ─── AI Assignment Suggestion Component ──────────────────────────────────────

interface AssignmentSuggestion {
  department: string;
  teamMember: string;
  memberRole: string;
  confidence: 'High' | 'Medium' | 'Low';
  rationale: string;
  workloadNote: string;
  skillMatch: string;
}

interface AIAssignmentSuggestionProps {
  ticket: Escalation;
  users: UserProfile[];
  onApplySuggestion: (department: string, memberName: string) => void;
}

function AIAssignmentSuggestion({ ticket, users, onApplySuggestion }: AIAssignmentSuggestionProps) {
  const [suggestion, setSuggestion] = useState<AssignmentSuggestion | null>(null);
  const [rawText, setRawText] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);
  const { response, isLoading, error, sendMessage } = useChat('GEMINI', 'gemini/gemini-2.5-flash', false);
  const [hasFetched, setHasFetched] = useState(false);

  const cacheKey = `esc_assign_${ticket.ticket_id}`;

  const generateSuggestion = useCallback(() => {
    if (hasFetched) return;
    setHasFetched(true);

    try {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < 60 * 60 * 1000) {
          setSuggestion(data);
          return;
        }
      }
    } catch { /* ignore */ }

    const teamList = users.length > 0
      ? users.map(u => `- ${u.first_name} ${u.last_name} (${u.user_role?.replace(/_/g, ' ')})`).join('\n')
      : 'No team members available';

    const assignmentHistory = (ticket.assignment_history || [])
      .map(a => `${a.assigned_to} (${a.reason})`)
      .join(', ') || 'No prior assignments';

    sendMessage([
      {
        role: 'system',
        content: `You are an intelligent escalation routing AI for NextRoof 360, a roofing company. Your job is to analyze escalation tickets and recommend the best department and team member to handle them.

Departments available: Logistics, Quality Control, Billing, Finance, Operations, Executive, Warranty, Project_Manager

Consider:
1. SKILLS: Match ticket type to department expertise (billing issues → Billing/Finance, delivery/materials → Logistics, inspection issues → Quality Control, general ops → Operations, critical/company-wide → Executive)
2. WORKLOAD: Prefer team members not recently assigned (check assignment history)
3. PERFORMANCE: Prefer team members whose role aligns with the ticket's nature
4. PRIORITY: High priority tickets should go to senior roles or Executive

Respond ONLY with valid JSON in this exact format (no markdown, no extra text):
{
  "department": "Department Name",
  "teamMember": "Full Name",
  "memberRole": "Their Role",
  "confidence": "High|Medium|Low",
  "rationale": "1-2 sentence explanation of why this person/dept is best",
  "workloadNote": "Brief note on their current load or availability",
  "skillMatch": "Specific skill or expertise that matches this ticket"
}`,
      },
      {
        role: 'user',
        content: `Ticket: ${ticket.ticket_id}
Title: ${ticket.title}
Department: ${ticket.source_department}
Priority: ${ticket.priority}
Status: ${ticket.status}
Description: ${ticket.description || 'No description provided'}

Prior Assignment History: ${assignmentHistory}

Available Team Members:
${teamList}

Suggest the optimal department and team member to handle this escalation.`,
      },
    ], { temperature: 0.3, max_tokens: 400 });
  }, [ticket, users, hasFetched, sendMessage, cacheKey]);

  useEffect(() => {
    generateSuggestion();
  }, [generateSuggestion]);

  useEffect(() => {
    if (response && !isLoading) {
      setRawText(response);
      try {
        // Strip markdown code fences if present
        const cleaned = response.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        const parsed: AssignmentSuggestion = JSON.parse(cleaned);
        setSuggestion(parsed);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify({ data: parsed, ts: Date.now() }));
        } catch { /* ignore */ }
      } catch {
        // If JSON parse fails, show raw text gracefully
        setSuggestion(null);
      }
    }
  }, [response, isLoading, cacheKey]);

  const handleApply = () => {
    if (!suggestion) return;
    setApplied(true);
    onApplySuggestion(suggestion.department, suggestion.teamMember);
  };

  const handleRefresh = () => {
    setHasFetched(false);
    setSuggestion(null);
    setRawText(null);
    setApplied(false);
    try { sessionStorage.removeItem(cacheKey); } catch { /* ignore */ }
  };

  const confidenceColor = suggestion?.confidence === 'High'
    ? { bg: '#DCFCE7', text: '#10B981', dot: '#10B981' }
    : suggestion?.confidence === 'Medium'
    ? { bg: '#FEF3C7', text: '#F59E0B', dot: '#F59E0B' }
    : { bg: '#FEE2E2', text: '#EF4444', dot: '#EF4444' };

  return (
    <section
      className="rounded-xl overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #F5F3FF 0%, #EFF6FF 100%)', border: '1px solid #DDD6FE' }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-3 border-b" style={{ borderColor: '#DDD6FE', background: 'rgba(124,58,237,0.04)' }}>
        <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)' }}>
          <Icon name="UserGroupIcon" size={12} className="text-white" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#4F46E5' }}>
          SmartAssign™ AI Routing Suggestion
        </span>
        {isLoading && (
          <div className="w-3 h-3 rounded-full border border-[#7C3AED] border-t-transparent animate-spin ml-auto" />
        )}
        {!isLoading && (suggestion || rawText) && (
          <button
            onClick={handleRefresh}
            className="ml-auto flex items-center gap-1 text-xs hover:opacity-70 transition-opacity"
            style={{ color: '#4F46E5' }}
          >
            <Icon name="ArrowPathIcon" size={11} />
            Refresh
          </button>
        )}
      </div>

      {/* Body */}
      <div className="px-5 py-4">
        {isLoading ? (
          <div className="space-y-2.5">
            <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(124,58,237,0.1)', width: '80%' }} />
            <div className="h-3 rounded-full animate-pulse" style={{ background: 'rgba(124,58,237,0.07)', width: '65%' }} />
            <div className="mt-3 h-3 rounded-full animate-pulse" style={{ background: 'rgba(79,70,229,0.1)', width: '55%' }} />
            <p className="text-[10px] mt-1" style={{ color: '#A78BFA' }}>Analyzing ticket and team availability…</p>
          </div>
        ) : suggestion ? (
          <div className="space-y-3">
            {/* Suggestion Card */}
            <div className="flex items-start gap-3 p-3 rounded-xl border" style={{ background: '#FFFFFF', borderColor: '#EDE9FE' }}>
              {/* Department Badge */}
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: getDeptColor(suggestion.department) + '20' }}>
                  <Icon name="BuildingOfficeIcon" size={18} style={{ color: getDeptColor(suggestion.department) }} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: getDeptColor(suggestion.department) + '20', color: getDeptColor(suggestion.department) }}>
                    {suggestion.department}
                  </span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: confidenceColor.bg, color: confidenceColor.text }}>
                    <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: confidenceColor.dot }} />
                    {suggestion.confidence} Confidence
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)' }}>
                    {getInitials(suggestion.teamMember)}
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: '#1E1B4B' }}>{suggestion.teamMember}</p>
                    <p className="text-[11px] text-gray-400">{suggestion.memberRole}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 gap-2">
              {/* Rationale */}
              <div className="flex items-start gap-2 rounded-lg px-3 py-2" style={{ background: 'rgba(79,70,229,0.06)', border: '1px solid rgba(79,70,229,0.12)' }}>
                <Icon name="LightBulbIcon" size={14} style={{ color: '#4F46E5', flexShrink: 0, marginTop: 1 }} />
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#4F46E5' }}>Why This Person</p>
                  <p className="text-xs leading-relaxed" style={{ color: '#3730A3' }}>{suggestion.rationale}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Skill Match */}
                <div className="flex items-start gap-2 rounded-lg px-3 py-2" style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.15)' }}>
                  <Icon name="CheckBadgeIcon" size={13} style={{ color: '#10B981', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#10B981' }}>Skill Match</p>
                    <p className="text-[11px] leading-relaxed" style={{ color: '#065F46' }}>{suggestion.skillMatch}</p>
                  </div>
                </div>
                {/* Workload */}
                <div className="flex items-start gap-2 rounded-lg px-3 py-2" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.15)' }}>
                  <Icon name="ChartBarIcon" size={13} style={{ color: '#F59E0B', flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wide mb-0.5" style={{ color: '#F59E0B' }}>Workload</p>
                    <p className="text-[11px] leading-relaxed" style={{ color: '#92400E' }}>{suggestion.workloadNote}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <button
              onClick={handleApply}
              disabled={applied}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-bold transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ background: applied ? '#10B981' : 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)' }}
            >
              <Icon name={applied ? 'CheckCircleIcon' : 'UserPlusIcon'} size={16} />
              {applied ? 'Suggestion Applied — Reassign Modal Opened' : `Assign to ${suggestion.teamMember} (${suggestion.department})`}
            </button>
          </div>
        ) : rawText ? (
          // Fallback: show raw AI text if JSON parse failed
          <div className="space-y-2">
            <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1">AI Routing Suggestion</p>
            <p className="text-sm leading-relaxed" style={{ color: '#1E1B4B' }}>{rawText}</p>
          </div>
        ) : error ? (
          <p className="text-xs text-red-500">Unable to generate suggestion. Please try refreshing.</p>
        ) : null}
      </div>
    </section>
  );
}

// ─── New Escalation Modal ─────────────────────────────────────────────────────

interface NewEscalationModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function NewEscalationModal({ onClose, onCreated }: NewEscalationModalProps) {
  const supabase = createClient();
  const { currentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    source_department: 'Operations',
    priority: 'medium',
    affected_job_id: '',
    affected_job_address: '',
  });
  const [escalateDepts, setEscalateDepts] = useState<string[]>(['Operations']);

  const DEPARTMENTS = ['Logistics', 'Quality Control', 'Billing', 'Finance', 'Operations', 'Executive', 'Warranty', 'Project_Manager'];

  const toggleDept = (dept: string) => {
    setEscalateDepts(prev =>
      prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (escalateDepts.length === 0) return;
    setSaving(true);
    const now = new Date().toISOString();
    const actorName = `${currentUser.first_name} ${currentUser.last_name}`;

    try {
      for (const dept of escalateDepts) {
        const ticketNum = `ESC-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
        const newEsc = {
          ticket_id: ticketNum,
          title: form.title,
          description: `${form.description}${escalateDepts.length > 1 ? `\n\nEscalated to: ${escalateDepts.join(', ')}` : ''}`,
          source_department: dept,
          priority: form.priority,
          status: 'open',
          assigned_to_name: actorName,
          reported_by_name: actorName,
          affected_job_id: form.affected_job_id,
          affected_job_address: form.affected_job_address,
          contact_directory: {},
          assignment_history: [{ date_time: now, assigned_from: 'Unassigned', assigned_to: actorName, reason: 'Initial ticket creation' }],
          timeline_log: [{ timestamp: now, actor: actorName, action: `Ticket created and sent to ${dept}`, type: 'created' }],
        };
        try {
          await supabase.from('escalations').insert(newEsc);
        } catch { /* use mock */ }
      }
    } finally {
      setSaving(false);
      onCreated();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold" style={{ color: '#1C2E5E' }}>New Escalation Ticket</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-all">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Title *</label>
            <input required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]" placeholder="Brief description of the issue" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] resize-none" placeholder="Detailed description..." />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">
              Escalate To <span className="text-red-500">*</span>
              <span className="ml-1 text-[11px] font-normal text-gray-400">(select one or more)</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {DEPARTMENTS.map(dept => {
                const selected = escalateDepts.includes(dept);
                return (
                  <button
                    key={dept}
                    type="button"
                    onClick={() => toggleDept(dept)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-semibold transition-all text-left ${
                      selected
                        ? 'border-[#E89A3C] bg-blue-50 text-[#1C2E5E]'
                        : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300 hover:bg-gray-100'
                    }`}
                  >
                    <div className={`w-3.5 h-3.5 rounded flex items-center justify-center flex-shrink-0 border ${
                      selected ? 'bg-[#E89A3C] border-[#E89A3C]' : 'border-gray-300 bg-white'
                    }`}>
                      {selected && (
                        <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    {dept}
                  </button>
                );
              })}
            </div>
            {escalateDepts.length > 0 && (
              <p className="text-[11px] text-[#E89A3C] mt-2 font-medium">
                {escalateDepts.length} ticket{escalateDepts.length > 1 ? 's' : ''} will be created: {escalateDepts.join(', ')}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Priority</label>
            <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]">
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Affected Job ID</label>
              <input value={form.affected_job_id} onChange={e => setForm(p => ({ ...p, affected_job_id: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]" placeholder="J-XXXX" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1">Job Address</label>
              <input value={form.affected_job_address} onChange={e => setForm(p => ({ ...p, affected_job_address: e.target.value }))}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C]" placeholder="123 Main St, City, ST" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
            <button type="submit" disabled={saving || escalateDepts.length === 0} className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-60" style={{ background: '#E89A3C' }}>
              {saving ? 'Creating...' : `Create ${escalateDepts.length > 1 ? escalateDepts.length + ' Tickets' : 'Ticket'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Reassign Modal ───────────────────────────────────────────────────────────

interface ReassignModalProps {
  ticket: Escalation;
  onClose: () => void;
  onReassigned: (ticketId: string, newAssignee: string) => void;
}

function ReassignModal({ ticket, onClose, onReassigned }: ReassignModalProps) {
  const supabase = createClient();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [selected, setSelected] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await supabase.from('user_profiles').select('id, first_name, last_name, user_role').limit(20);
        if (data && data.length > 0) setUsers(data);
        else setUsers([]);
      } catch {
        setUsers([]);
      }
    };
    fetchUsers();
  }, [supabase]);

  const handleReassign = async () => {
    if (!selected || !reason) return;
    setSaving(true);
    const selectedUser = users.find(u => u.id === selected);
    const newName = selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : selected;
    const now = new Date().toISOString();
    const newHistory = [...(ticket.assignment_history || []), {
      date_time: now,
      assigned_from: ticket.assigned_to_name,
      assigned_to: newName,
      reason,
    }];
    const newTimeline = [...(ticket.timeline_log || []), {
      timestamp: now,
      actor: `${currentUser.first_name} ${currentUser.last_name}`,
      action: `Reassigned from ${ticket.assigned_to_name} to ${newName}. Reason: ${reason}`,
      type: 'reassigned' as const,
    }];
    try {
      await supabase.from('escalations').update({
        assigned_to_id: selected,
        assigned_to_name: newName,
        assignment_history: newHistory,
        timeline_log: newTimeline,
        updated_at: now,
      }).eq('ticket_id', ticket.ticket_id);
    } catch { /* mock */ }
    setSaving(false);
    onReassigned(ticket.ticket_id, newName);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold" style={{ color: '#1C2E5E' }}>Reassign Ticket</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-all">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500">Reassigning: <span className="font-semibold text-gray-700">{ticket.ticket_id} — {ticket.title}</span></p>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-2">Select New Assignee</label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {users.map(u => (
                <button key={u.id} onClick={() => setSelected(u.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all text-left ${selected === u.id ? 'border-[#E89A3C] bg-[#E89A3C]/5' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
                    {getInitials(`${u.first_name} ${u.last_name}`)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-gray-400">{u.user_role?.replace(/_/g, ' ')}</p>
                  </div>
                  {selected === u.id && <Icon name="CheckCircleIcon" size={18} className="ml-auto" style={{ color: '#E89A3C' }} />}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Reason for Reassignment *</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] resize-none" placeholder="Explain why you are reassigning..." />
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
            <button onClick={handleReassign} disabled={!selected || !reason || saving}
              className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: '#1C2E5E' }}>
              {saving ? 'Reassigning...' : 'Confirm Reassign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Change Priority Modal ────────────────────────────────────────────────────

interface ChangePriorityModalProps {
  ticket: Escalation;
  onClose: () => void;
  onChanged: (ticketId: string, newPriority: string) => void;
}

function ChangePriorityModal({ ticket, onClose, onChanged }: ChangePriorityModalProps) {
  const supabase = createClient();
  const { currentUser } = useAuth();
  const [selected, setSelected] = useState(ticket.priority);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const priorities = [
    { value: 'high', label: 'High', color: '#EF4444', bg: '#FEE2E2' },
    { value: 'medium', label: 'Medium', color: '#F59E0B', bg: '#FEF3C7' },
    { value: 'low', label: 'Low', color: '#10B981', bg: '#DCFCE7' },
  ];

  const handleChange = async () => {
    if (!selected || selected === ticket.priority) return;
    setSaving(true);
    const now = new Date().toISOString();
    const newTimeline = [...(ticket.timeline_log || []), {
      timestamp: now,
      actor: `${currentUser.first_name} ${currentUser.last_name}`,
      action: `Priority changed from ${ticket.priority} to ${selected}${reason ? `. Reason: ${reason}` : ''}`,
      type: 'status_change' as const,
    }];
    try {
      await supabase.from('escalations').update({
        priority: selected,
        timeline_log: newTimeline,
        updated_at: now,
      }).eq('ticket_id', ticket.ticket_id);
    } catch { /* mock */ }
    setSaving(false);
    onChanged(ticket.ticket_id, selected);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold" style={{ color: '#1C2E5E' }}>Change Priority</h2>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 transition-all">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-500">Ticket: <span className="font-semibold text-gray-700">{ticket.ticket_id}</span></p>
          <div className="space-y-2">
            {priorities.map(p => (
              <button key={p.value} onClick={() => setSelected(p.value)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${selected === p.value ? 'border-[#E89A3C] shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                style={{ background: selected === p.value ? p.bg : '#FAFAFA' }}>
                <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: p.color }} />
                <span className="text-sm font-semibold" style={{ color: p.color }}>{p.label} Priority</span>
                {ticket.priority === p.value && <span className="ml-auto text-[10px] text-gray-400 font-medium">Current</span>}
                {selected === p.value && ticket.priority !== p.value && <Icon name="CheckCircleIcon" size={16} className="ml-auto" style={{ color: '#E89A3C' }} />}
              </button>
            ))}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Reason (optional)</label>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] resize-none"
              placeholder="Why are you changing the priority?" />
          </div>
          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all">Cancel</button>
            <button onClick={handleChange} disabled={selected === ticket.priority || saving}
              className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-50"
              style={{ background: '#F59E0B' }}>
              {saving ? 'Saving...' : 'Change Priority'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Contact Mini Card ────────────────────────────────────────────────────────

function ContactMiniCard({ label, person }: { label: string; person: ContactPerson }) {
  return (
    <div className="flex-1 min-w-[160px] rounded-xl p-4 border border-gray-100" style={{ background: '#F8F9FA' }}>
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">{label}</p>
      <div className="flex items-start gap-2.5">
        <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
          {getInitials(person.name)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800 truncate">{person.name}</p>
          <p className="text-xs text-gray-400 truncate">{person.role}</p>
          {person.phone && <p className="text-xs text-gray-500 mt-0.5">{person.phone}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        {person.phone && (
          <button className="flex items-center gap-1 text-xs text-[#E89A3C] hover:underline">
            <Icon name="PhoneIcon" size={12} />
            <span>Call</span>
          </button>
        )}
        {person.email && (
          <button className="flex items-center gap-1 text-xs text-[#1C2E5E] hover:underline">
            <Icon name="EnvelopeIcon" size={12} />
            <span>Email</span>
          </button>
        )}
        <button className="flex items-center gap-1 text-xs text-gray-500 hover:underline ml-auto">
          <Icon name="ChatBubbleLeftIcon" size={12} />
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function EscalationManagementInteractive() {
  const { currentUser } = useAuth();
  const supabase = createClient();
  const router = useRouter();
  const searchParams = useSearchParams();

  const [escalations, setEscalations] = useState<Escalation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<Escalation | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [showNewModal, setShowNewModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [teamUsers, setTeamUsers] = useState<UserProfile[]>([]);
  const [showChangePriorityModal, setShowChangePriorityModal] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── Fetch Team Users (for AI suggestion) ──────────────────────────────────

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data } = await supabase.from('user_profiles').select('id, first_name, last_name, user_role').limit(30);
        if (data && data.length > 0) setTeamUsers(data);
        else setTeamUsers([]);
      } catch {
        setTeamUsers([]);
      }
    };
    loadUsers();
  }, [supabase]);

  // ── Apply AI suggestion → open reassign modal ──────────────────────────────

  const handleApplySuggestion = useCallback((_department: string, _memberName: string) => {
    setShowReassignModal(true);
  }, []);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchEscalations = useCallback(async () => {
    try {
      const { data, error } = await supabase.from('escalations').select('*').order('created_at', { ascending: false });
      if (data && data.length > 0) {
        setEscalations(data);
      } else {
        setEscalations([]);
      }
    } catch {
      setEscalations([]);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchEscalations();
    const sub = supabase
      .channel('escalations-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'escalations' }, fetchEscalations)
      .subscribe();
    return () => { supabase.removeChannel(sub); };
  }, [fetchEscalations, supabase]);

  // ── Pre-select ticket from URL param ──────────────────────────────────────

  useEffect(() => {
    const ticketId = searchParams?.get('ticket');
    if (ticketId && escalations.length > 0) {
      const found = escalations.find(e => e.ticket_id === ticketId || e.id === ticketId);
      if (found) setSelectedTicket(found);
    }
  }, [searchParams, escalations]);

  // ── Filtered list ──────────────────────────────────────────────────────────

  const filtered = escalations.filter(e => {
    if (showArchived) {
      // Archived view: only closed tickets
      return e.status === 'closed';
    }
    // Active view: exclude closed tickets, apply status/priority filters
    if (e.status === 'closed') return false;
    const statusMatch = filterStatus === 'all' || e.status === filterStatus;
    const priorityMatch = filterPriority === 'all' || e.priority === filterPriority;
    return statusMatch && priorityMatch;
  });

  // ── Update status ──────────────────────────────────────────────────────────

  const handleUpdateStatus = async (newStatus: string) => {
    if (!selectedTicket) return;
    const now = new Date().toISOString();
    const newTimeline = [...(selectedTicket.timeline_log || []), {
      timestamp: now,
      actor: `${currentUser.first_name} ${currentUser.last_name}`,
      action: `Status changed to ${getStatusLabel(newStatus)}`,
      type: 'status_change' as const,
    }];
    const updated = { ...selectedTicket, status: newStatus, timeline_log: newTimeline, updated_at: now };
    try {
      await supabase.from('escalations').update({ status: newStatus, timeline_log: newTimeline, updated_at: now }).eq('ticket_id', selectedTicket.ticket_id);
    } catch { /* mock */ }
    setEscalations(prev => prev.map(e => e.ticket_id === selectedTicket.ticket_id ? updated : e));
    setSelectedTicket(updated);
    setStatusDropdownOpen(false);
    showToast(`Status updated to ${getStatusLabel(newStatus)}`);
  };

  // ── Escalate further ──────────────────────────────────────────────────────

  const handleEscalateFurther = async () => {
    if (!selectedTicket) return;
    const now = new Date().toISOString();
    const newTimeline = [...(selectedTicket.timeline_log || []), {
      timestamp: now,
      actor: `${currentUser.first_name} ${currentUser.last_name}`,
      action: 'Ticket escalated further to senior management',
      type: 'escalated' as const,
    }];
    const updated = { ...selectedTicket, priority: 'high', timeline_log: newTimeline, updated_at: now };
    try {
      await supabase.from('escalations').update({ priority: 'high', timeline_log: newTimeline, updated_at: now }).eq('ticket_id', selectedTicket.ticket_id);
    } catch { /* mock */ }
    setEscalations(prev => prev.map(e => e.ticket_id === selectedTicket.ticket_id ? updated : e));
    setSelectedTicket(updated);
    showToast('Ticket escalated to senior management', 'error');
  };

  // ── Add note ──────────────────────────────────────────────────────────────

  const handleAddNote = async () => {
    if (!selectedTicket || !noteText.trim()) return;
    setSavingNote(true);
    const now = new Date().toISOString();
    const newTimeline = [...(selectedTicket.timeline_log || []), {
      timestamp: now,
      actor: `${currentUser.first_name} ${currentUser.last_name}`,
      action: noteText.trim(),
      type: 'note' as const,
    }];
    const updated = { ...selectedTicket, timeline_log: newTimeline, updated_at: now };
    try {
      await supabase.from('escalations').update({ timeline_log: newTimeline, updated_at: now }).eq('ticket_id', selectedTicket.ticket_id);
    } catch { /* mock */ }
    setEscalations(prev => prev.map(e => e.ticket_id === selectedTicket.ticket_id ? updated : e));
    setSelectedTicket(updated);
    setNoteText('');
    setSavingNote(false);
    showToast('Note added to timeline');
  };

  // ── After reassign ────────────────────────────────────────────────────────

  const handleReassigned = (ticketId: string, newAssignee: string) => {
    const now = new Date().toISOString();
    setEscalations(prev => prev.map(e => {
      if (e.ticket_id !== ticketId) return e;
      const updated = { ...e, assigned_to_name: newAssignee, updated_at: now };
      if (selectedTicket?.ticket_id === ticketId) setSelectedTicket(updated);
      return updated;
    }));
    showToast(`Ticket reassigned to ${newAssignee}`);
  };

  // ── After priority change ─────────────────────────────────────────────────

  const handlePriorityChanged = (ticketId: string, newPriority: string) => {
    const now = new Date().toISOString();
    setEscalations(prev => prev.map(e => {
      if (e.ticket_id !== ticketId) return e;
      const updated = { ...e, priority: newPriority, updated_at: now };
      if (selectedTicket?.ticket_id === ticketId) setSelectedTicket(updated);
      return updated;
    }));
    showToast(`Priority changed to ${newPriority}`);
  };

  // ── Close ticket ──────────────────────────────────────────────────────────

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;
    const now = new Date().toISOString();
    const newTimeline = [...(selectedTicket.timeline_log || []), {
      timestamp: now,
      actor: `${currentUser.first_name} ${currentUser.last_name}`,
      action: 'Ticket closed',
      type: 'status_change' as const,
    }];
    const updated = { ...selectedTicket, status: 'closed', timeline_log: newTimeline, updated_at: now };
    try {
      await supabase.from('escalations').update({ status: 'closed', timeline_log: newTimeline, updated_at: now }).eq('ticket_id', selectedTicket.ticket_id);
    } catch { /* mock */ }
    setEscalations(prev => prev.map(e => e.ticket_id === selectedTicket.ticket_id ? updated : e));
    setSelectedTicket(updated);
    showToast('Ticket closed successfully');
  };

  const userInitials = `${currentUser.first_name.charAt(0)}${currentUser.last_name.charAt(0)}`.toUpperCase();
  const userName = `${currentUser.first_name} ${currentUser.last_name}`;

  // ── Build comment thread from timeline notes ──────────────────────────────

  const commentThread: Comment[] = selectedTicket
    ? (selectedTicket.timeline_log || [])
        .filter(e => e.type === 'note' || e.type === 'reassigned' || e.type === 'status_change' || e.type === 'created')
        .map((e, i) => ({
          id: `${i}`,
          author: e.actor,
          authorInitials: getInitials(e.actor || 'UN'),
          text: e.action,
          timestamp: e.timestamp,
          type: (e.type === 'note' || e.type === 'created') ? 'comment' : 'action',
          actionType: e.type === 'reassigned' ? 'reassigned' : e.type === 'status_change' ? 'priority_changed' : undefined,
        }))
    : [];

  return (
    <div className="min-h-screen" style={{ background: '#F8F9FA' }}>

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-semibold transition-all ${toast.type === 'success' ? 'bg-[#10B981]' : 'bg-[#EF4444]'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationCircleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <header className="sticky top-[60px] z-30 flex items-center justify-between px-6 py-3 border-b border-gray-200"
        style={{ background: '#FFFFFF', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <div>
          <h1 className="text-xl font-bold" style={{ color: '#1C2E5E', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Escalation Management
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">Monitor, assign, and resolve critical issues</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowNewModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90 active:scale-95"
            style={{ background: '#E89A3C' }}>
            <Icon name="PlusIcon" size={16} />
            New Escalation
          </button>
          <button className="w-9 h-9 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-all relative">
            <Icon name="BellIcon" size={20} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500" />
          </button>
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
            {userInitials}
          </div>
        </div>
      </header>

      {/* ── Two-Panel Layout ──────────────────────────────────────────────── */}
      <div className="flex h-[calc(100vh-120px)]">

        {/* ── Left Panel: Ticket List (40%) ─────────────────────────────── */}
        <div className="w-full lg:w-[40%] flex flex-col border-r border-gray-200 bg-white overflow-hidden flex-shrink-0">

          {/* Filters */}
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            {!showArchived && (
              <>
                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                  className="flex-1 min-w-[120px] border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#E89A3C]">
                  <option value="all">All Statuses</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                </select>
                <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}
                  className="flex-1 min-w-[120px] border border-gray-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#E89A3C]">
                  <option value="all">All Priorities</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </>
            )}
            {showArchived && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-500">
                <Icon name="ArchiveBoxIcon" size={14} style={{ color: '#6B7280' }} />
                Archived (Closed) Tickets
              </span>
            )}
            <span className="text-xs text-gray-400 font-medium ml-auto">{filtered.length} tickets</span>
            <button
              onClick={() => setShowArchived(p => !p)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all ${showArchived ? 'border-[#6B7280] bg-gray-100 text-gray-700' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'}`}
            >
              <Icon name="ArchiveBoxIcon" size={13} />
              {showArchived ? 'Back to Active' : 'View Archived'}
            </button>
          </div>

          {/* Ticket List */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-24 rounded-xl animate-pulse" style={{ background: '#F3F4F6' }} />
              ))
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Icon name="CheckCircleIcon" size={40} style={{ color: '#10B981' }} />
                <p className="text-sm font-semibold text-gray-500 mt-3">No tickets match filters</p>
              </div>
            ) : (
              filtered.map(ticket => {
                const pColor = getPriorityColor(ticket.priority);
                const sColor = getStatusColor(ticket.status);
                const isSelected = selectedTicket?.ticket_id === ticket.ticket_id;
                return (
                  <button key={ticket.ticket_id} onClick={() => setSelectedTicket(ticket)}
                    className={`w-full text-left rounded-xl p-3.5 border transition-all hover:shadow-sm ${isSelected ? 'border-[#E89A3C] shadow-sm' : 'border-gray-100 hover:border-gray-200'}`}
                    style={{ background: isSelected ? '#F0FBFF' : '#FFFFFF' }}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-gray-400">{ticket.ticket_id}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: getDeptColor(ticket.source_department) + '20', color: getDeptColor(ticket.source_department) }}>
                          {ticket.source_department}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: pColor.dot }} />
                        <span className="text-[10px] font-bold uppercase" style={{ color: pColor.text }}>{ticket.priority}</span>
                      </div>
                    </div>
                    <p className="text-sm font-semibold text-gray-800 leading-tight mb-2 line-clamp-2">{ticket.title}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
                          style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
                          {getInitials(ticket.assigned_to_name || 'UN')}
                        </div>
                        <span className="text-xs text-gray-500">{ticket.assigned_to_name || 'Unassigned'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: sColor.bg, color: sColor.text }}>
                          {getStatusLabel(ticket.status)}
                        </span>
                        <span className="text-[10px] text-gray-400">{timeAgo(ticket.created_at)}</span>
                      </div>
                    </div>
                    {/* Due timestamps row */}
                    {(ticket.response_due || ticket.resolution_due) && (
                      <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-50">
                        {ticket.response_due && (
                          <div className="flex items-center gap-1">
                            <Icon name="ClockIcon" size={10} style={{ color: isBreached(ticket.response_due) ? '#EF4444' : '#9CA3AF' }} />
                            <span className="text-[10px] font-semibold" style={{ color: isBreached(ticket.response_due) ? '#EF4444' : '#9CA3AF' }}>
                              Resp: {formatDueDate(ticket.response_due)}
                              {isBreached(ticket.response_due) && ' ⚠'}
                            </span>
                          </div>
                        )}
                        {ticket.resolution_due && (
                          <div className="flex items-center gap-1">
                            <Icon name="CheckCircleIcon" size={10} style={{ color: isBreached(ticket.resolution_due) ? '#EF4444' : '#9CA3AF' }} />
                            <span className="text-[10px] font-semibold" style={{ color: isBreached(ticket.resolution_due) ? '#EF4444' : '#9CA3AF' }}>
                              Res: {formatDueDate(ticket.resolution_due)}
                              {isBreached(ticket.resolution_due) && ' ⚠'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right Panel: Detail View (60%) ────────────────────────────── */}
        <div className="hidden lg:flex flex-1 flex-col overflow-hidden">
          {!selectedTicket ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#F0FBFF' }}>
                <Icon name="ExclamationTriangleIcon" size={32} style={{ color: '#E89A3C' }} />
              </div>
              <h3 className="text-lg font-bold text-gray-700 mb-2">Select a Ticket</h3>
              <p className="text-sm text-gray-400 max-w-xs">Click any escalation ticket from the left panel to view full details, history, and take action.</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">

              {/* Detail Header */}
              <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-bold text-gray-400">{selectedTicket.ticket_id}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: getDeptColor(selectedTicket.source_department) + '20', color: getDeptColor(selectedTicket.source_department) }}>
                        {selectedTicket.source_department}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: getPriorityColor(selectedTicket.priority).bg, color: getPriorityColor(selectedTicket.priority).text }}>
                        {selectedTicket.priority.toUpperCase()} PRIORITY
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: getStatusColor(selectedTicket.status).bg, color: getStatusColor(selectedTicket.status).text }}>
                        {getStatusLabel(selectedTicket.status)}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold leading-tight" style={{ color: '#1C2E5E' }}>{selectedTicket.title}</h2>
                    <p className="text-xs text-gray-400 mt-0.5">Opened {formatDateTime(selectedTicket.created_at)} · Updated {timeAgo(selectedTicket.updated_at)}</p>
                    {/* SLA Due Timestamps in detail header */}
                    {(selectedTicket.response_due || selectedTicket.resolution_due) && (
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        {selectedTicket.response_due && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border"
                            style={{
                              borderColor: isBreached(selectedTicket.response_due) ? '#FCA5A5' : '#E5E7EB',
                              background: isBreached(selectedTicket.response_due) ? '#FEF2F2' : '#F9FAFB',
                            }}>
                            <Icon name="ClockIcon" size={12} style={{ color: isBreached(selectedTicket.response_due) ? '#EF4444' : '#6B7280' }} />
                            <span className="text-[11px] font-semibold" style={{ color: isBreached(selectedTicket.response_due) ? '#EF4444' : '#6B7280' }}>
                              Response Due: {formatDueDate(selectedTicket.response_due)}
                              {isBreached(selectedTicket.response_due) && (
                                <span className="ml-1 font-bold text-red-600">BREACHED</span>
                              )}
                            </span>
                          </div>
                        )}
                        {selectedTicket.resolution_due && (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border"
                            style={{
                              borderColor: isBreached(selectedTicket.resolution_due) ? '#FCA5A5' : '#E5E7EB',
                              background: isBreached(selectedTicket.resolution_due) ? '#FEF2F2' : '#F9FAFB',
                            }}>
                            <Icon name="CheckCircleIcon" size={12} style={{ color: isBreached(selectedTicket.resolution_due) ? '#EF4444' : '#6B7280' }} />
                            <span className="text-[11px] font-semibold" style={{ color: isBreached(selectedTicket.resolution_due) ? '#EF4444' : '#6B7280' }}>
                              Resolution Due: {formatDueDate(selectedTicket.resolution_due)}
                              {isBreached(selectedTicket.resolution_due) && (
                                <span className="ml-1 font-bold text-red-600">BREACHED</span>
                              )}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Action Bar */}
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                    {/* Update Status Dropdown */}
                    <div className="relative">
                      <button onClick={() => setStatusDropdownOpen(p => !p)}
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold transition-all hover:opacity-90"
                        style={{ background: '#E89A3C' }}>
                        <Icon name="ArrowPathIcon" size={14} />
                        Update Status
                        <Icon name="ChevronDownIcon" size={12} />
                      </button>
                      {statusDropdownOpen && (
                        <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-100 z-20 min-w-[160px] overflow-hidden">
                          {['open', 'in_progress', 'resolved', 'closed'].map(s => (
                            <button key={s} onClick={() => handleUpdateStatus(s)}
                              className={`w-full flex items-center gap-2 px-4 py-3 text-xs font-semibold hover:bg-gray-50 transition-all text-left ${selectedTicket.status === s ? 'bg-gray-50' : ''}`}>
                              <span className="w-2 h-2 rounded-full" style={{ background: getStatusColor(s).text }} />
                              {getStatusLabel(s)}
                              {selectedTicket.status === s && <Icon name="CheckIcon" size={12} className="ml-auto text-gray-400" />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Reassign */}
                    <button onClick={() => setShowReassignModal(true)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all hover:bg-gray-50"
                      style={{ borderColor: '#1C2E5E', color: '#1C2E5E' }}>
                      <Icon name="UserIcon" size={14} />
                      Reassign
                    </button>
                    {/* Escalate Further */}
                    <button onClick={handleEscalateFurther}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold transition-all hover:opacity-90"
                      style={{ background: '#EF4444' }}>
                      <Icon name="ExclamationTriangleIcon" size={14} />
                      Escalate Further
                    </button>
                  </div>
                </div>
              </div>

              <div className="px-6 py-5 space-y-6">

                {/* ── AI Escalation Summary (top of detail panel) ─────────── */}
                <AIEscalationSummary key={selectedTicket.ticket_id} ticket={selectedTicket} />

                {/* AI Assignment Suggestion */}
                <AIAssignmentSuggestion key={`assign-${selectedTicket.ticket_id}`} ticket={selectedTicket} users={teamUsers} onApplySuggestion={handleApplySuggestion} />

                {/* Contact Mini-Cards */}
                {selectedTicket.contact_directory && Object.keys(selectedTicket.contact_directory).length > 0 && (
                  <section>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Contact Directory</h3>
                    <div className="flex gap-3 flex-wrap">
                      {selectedTicket.contact_directory.reported_by && (
                        <ContactMiniCard label="Reported By" person={selectedTicket.contact_directory.reported_by} />
                      )}
                      {selectedTicket.contact_directory.assigned_to && (
                        <ContactMiniCard label="Assigned To" person={selectedTicket.contact_directory.assigned_to} />
                      )}
                      {selectedTicket.contact_directory.department_head && (
                        <ContactMiniCard label="Department Head" person={selectedTicket.contact_directory.department_head} />
                      )}
                      {selectedTicket.contact_directory.affected_job_pm && (
                        <ContactMiniCard label="Affected Job PM" person={selectedTicket.contact_directory.affected_job_pm} />
                      )}
                    </div>
                  </section>
                )}

                {/* Ticket Details */}
                <section className="rounded-xl p-5 border border-gray-100" style={{ background: '#FFFFFF' }}>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">Ticket Details</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 mb-1">Description</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{selectedTicket.description || 'No description provided.'}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-50">
                      <div>
                        <p className="text-xs font-semibold text-gray-400 mb-1">Source Department</p>
                        <span className="text-xs px-2 py-1 rounded-full font-semibold" style={{ background: getDeptColor(selectedTicket.source_department) + '20', color: getDeptColor(selectedTicket.source_department) }}>
                          {selectedTicket.source_department}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-400 mb-1">Affected Job ID</p>
                        <p className="text-sm font-bold" style={{ color: '#1C2E5E' }}>{selectedTicket.affected_job_id || '—'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs font-semibold text-gray-400 mb-1">Job Address</p>
                        <div className="flex items-center gap-1.5">
                          <Icon name="MapPinIcon" size={14} style={{ color: '#E89A3C' }} />
                          <p className="text-sm text-gray-700">{selectedTicket.affected_job_address || '—'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Assignment History */}
                {selectedTicket.assignment_history && selectedTicket.assignment_history.length > 0 && (
                  <section className="rounded-xl border border-gray-100 overflow-hidden" style={{ background: '#FFFFFF' }}>
                    <div className="px-5 py-3.5 border-b border-gray-100">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400">Assignment History</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr style={{ background: '#F8F9FA' }}>
                            <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Date / Time</th>
                            <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Assigned From</th>
                            <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Assigned To</th>
                            <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedTicket.assignment_history.map((entry, i) => (
                            <tr key={i} className="border-t border-gray-50 hover:bg-gray-50 transition-all">
                              <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{formatDateTime(entry.date_time)}</td>
                              <td className="px-4 py-3 font-medium text-gray-700">{entry.assigned_from}</td>
                              <td className="px-4 py-3 font-semibold" style={{ color: '#1C2E5E' }}>{entry.assigned_to}</td>
                              <td className="px-4 py-3 text-gray-500">{entry.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* Timeline Log */}
                {selectedTicket.timeline_log && selectedTicket.timeline_log.length > 0 && (
                  <section className="rounded-xl p-5 border border-gray-100" style={{ background: '#FFFFFF' }}>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">Timeline Log</h3>
                    <div className="relative">
                      <div className="absolute left-[15px] top-0 bottom-0 w-0.5" style={{ background: '#E5E7EB' }} />
                      <div className="space-y-4">
                        {[...selectedTicket.timeline_log].reverse().map((entry, i) => {
                          const color = getTimelineColor(entry.type);
                          const iconName = getTimelineIcon(entry.type);
                          return (
                            <div key={i} className="flex gap-4 relative">
                              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-2 border-white"
                                style={{ background: color + '20' }}>
                                <Icon name={iconName as any} size={14} style={{ color }} />
                              </div>
                              <div className="flex-1 pb-1">
                                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                  <span className="text-xs font-bold text-gray-700">{entry.actor}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold capitalize" style={{ background: color + '15', color }}>
                                    {entry.type?.replace('_', ' ')}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600 leading-relaxed">{entry.action}</p>
                                <p className="text-[10px] text-gray-400 mt-1">{formatDateTime(entry.timestamp)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </section>
                )}

                {/* ── Live Comment Thread ──────────────────────────────────── */}
                <section className="rounded-xl border border-gray-100 overflow-hidden" style={{ background: '#FFFFFF' }}>
                  {/* Thread Header */}
                  <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100" style={{ background: '#F8F9FA' }}>
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
                        <Icon name="ChatBubbleLeftRightIcon" size={12} className="text-white" />
                      </div>
                      <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#1C2E5E' }}>
                        Collaboration Thread
                      </h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: '#DBEAFE', color: '#E89A3C' }}>
                        {commentThread.length} {commentThread.length === 1 ? 'entry' : 'entries'}
                      </span>
                      <span className="flex items-center gap-1 text-[10px] font-semibold" style={{ color: '#10B981' }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        Live
                      </span>
                    </div>
                    {/* Inline Quick Actions */}
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setShowReassignModal(true)}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-all hover:shadow-sm active:scale-95"
                        style={{ borderColor: '#1C2E5E', color: '#1C2E5E', background: '#EFF6FF' }}
                        title="Reassign ticket"
                      >
                        <Icon name="UserPlusIcon" size={12} />
                        Reassign
                      </button>
                      <button
                        onClick={() => setShowChangePriorityModal(true)}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-all hover:shadow-sm active:scale-95"
                        style={{ borderColor: '#F59E0B', color: '#B45309', background: '#FFFBEB' }}
                        title="Change priority"
                      >
                        <Icon name="FlagIcon" size={12} />
                        Priority
                      </button>
                      <button
                        onClick={handleCloseTicket}
                        disabled={selectedTicket.status === 'closed'}
                        className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-all hover:shadow-sm active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ borderColor: '#6B7280', color: '#374151', background: '#F9FAFB' }}
                        title="Close ticket"
                      >
                        <Icon name="XCircleIcon" size={12} />
                        Close
                      </button>
                    </div>
                  </div>

                  {/* Comment Feed */}
                  <div className="px-5 py-4 space-y-4 max-h-72 overflow-y-auto" style={{ background: '#FAFAFA' }}>
                    {commentThread.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Icon name="ChatBubbleLeftIcon" size={28} style={{ color: '#D1D5DB' }} />
                        <p className="text-xs text-gray-400 mt-2">No comments yet. Be the first to add a note.</p>
                      </div>
                    ) : (
                      commentThread.map((comment) => {
                        const isCurrentUser = comment.author === userName;
                        const isAction = comment.type === 'action';
                        if (isAction) {
                          return (
                            <div key={comment.id} className="flex items-center gap-2 py-1">
                              <div className="flex-1 h-px" style={{ background: '#E5E7EB' }} />
                              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-semibold" style={{ background: '#F3F4F6', color: '#6B7280' }}>
                                <Icon name="ArrowPathIcon" size={10} />
                                <span>{comment.author}</span>
                                <span>·</span>
                                <span>{comment.text}</span>
                                <span>·</span>
                                <span>{timeAgo(comment.timestamp)}</span>
                              </div>
                              <div className="flex-1 h-px" style={{ background: '#E5E7EB' }} />
                            </div>
                          );
                        }
                        return (
                          <div key={comment.id} className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${isCurrentUser ? '' : ''}`}
                              style={{ background: isCurrentUser ? 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' : 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' }}>
                              {comment.authorInitials}
                            </div>
                            <div className={`flex-1 max-w-[75%] ${isCurrentUser ? 'items-end' : 'items-start'} flex flex-col`}>
                              <div className={`flex items-center gap-2 mb-1 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                                <span className="text-xs font-bold text-gray-700">{isCurrentUser ? 'You' : comment.author}</span>
                                <span className="text-[10px] text-gray-400">{timeAgo(comment.timestamp)}</span>
                              </div>
                              <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isCurrentUser ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
                                style={{
                                  background: isCurrentUser ? 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' : '#FFFFFF',
                                  color: isCurrentUser ? '#FFFFFF' : '#374151',
                                  border: isCurrentUser ? 'none' : '1px solid #E5E7EB',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                }}>
                                {comment.text}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Comment Input */}
                  <div className="px-5 py-4 border-t border-gray-100" style={{ background: '#FFFFFF' }}>
                    <div className="flex gap-3 items-end">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                        style={{ background: 'linear-gradient(135deg, #E89A3C 0%, #1C2E5E 100%)' }}>
                        {userInitials}
                      </div>
                      <div className="flex-1 relative">
                        <textarea
                          value={noteText}
                          onChange={e => setNoteText(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAddNote();
                            }
                          }}
                          rows={2}
                          className="w-full border border-gray-200 rounded-2xl px-4 py-2.5 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-[#E89A3C] resize-none"
                          style={{ background: '#F8F9FA' }}
                          placeholder="Add a comment… (Enter to send, Shift+Enter for new line)"
                        />
                        <button
                          onClick={handleAddNote}
                          disabled={!noteText.trim() || savingNote}
                          className="absolute right-3 bottom-3 w-7 h-7 rounded-full flex items-center justify-center text-white transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{ background: noteText.trim() ? '#E89A3C' : '#D1D5DB' }}
                        >
                          <Icon name="PaperAirplaneIcon" size={13} />
                        </button>
                      </div>
                    </div>
                    <p className="text-[10px] text-gray-400 mt-2 ml-11">
                      {savingNote ? 'Posting comment…' : 'Comments are visible to all team members on this ticket.'}
                    </p>
                  </div>
                </section>

              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showNewModal && (
        <NewEscalationModal onClose={() => setShowNewModal(false)} onCreated={fetchEscalations} />
      )}
      {showReassignModal && selectedTicket && (
        <ReassignModal ticket={selectedTicket} onClose={() => setShowReassignModal(false)} onReassigned={handleReassigned} />
      )}
      {showChangePriorityModal && selectedTicket && (
        <ChangePriorityModal ticket={selectedTicket} onClose={() => setShowChangePriorityModal(false)} onChanged={handlePriorityChanged} />
      )}
    </div>
  );
}
